import type { JSX, PropsWithChildren } from 'react';

import { Button, Text } from '@change/ds-core-ui/next';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import { useAsyncDownload } from 'src/app/shared/components/qrcode';
import { getPetitionQRCode } from 'src/app/shared/hooks/qrcode';

/**
 * Render a button that when clicked, downloads a QR code.
 * The QR code is fetched lazily when the button is clicked.
 */
export function QRCodeDownloadLink({
	children,
	slugOrId,
	size,
	type,
	filename,
	onDownload,
}: PropsWithChildren<{
	slugOrId: string;
	type: QrCodeType;
	size?: number;
	filename?: string;
	onDownload?: () => Promise<void>;
}>): JSX.Element {
	const utilityContext = useUtilityContext();

	const { actions } = useAsyncDownload({
		fetchData: async () => {
			const result = await getPetitionQRCode({
				slugOrId,
				type,
				size,
				utilityContext,
			});
			return { dataUri: result.imageBase64Uri };
		},
		filename: filename || 'petition-qrcode.png',
		onDownload,
	});

	return (
		<Button
			onClick={actions.handleDownload}
			variant="secondary"
			size="small"
			data-qa="qrcode-download-link"
			data-testid="qrcode-download-link"
		>
			<Text variant="body" size="medium" fontWeight="bold">
				{children}
			</Text>
		</Button>
	);
}
